import {
  ACCESS_TOKEN_KEY,
  BACKEND_URL,
  getQueryClient,
  REFRESH_TOKEN_KEY
} from '@lib/config';
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import { createTranslator } from 'use-intl/core';

import { dictionaries } from '~/get-dictionaries';
import { toast } from '~/modules/common/components/ui/use-toast';

import { setToken } from './tokens';
import { AUTH_ROUTES } from '../constants';
import { getRefreshToken, refreshRoute } from '../services/api/user';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig<unknown> {
  _retry?: boolean
}

const BASE_URL = BACKEND_URL;
const isServer = typeof window === 'undefined';

const queryClient = getQueryClient();
const cookiesInterceptor = async (req: InternalAxiosRequestConfig<unknown>) => {
  let language = 'en';
  // if (req.url !== 'users/login') {
  //   req.headers.Authorization = `Bearer ${await getToken(ACCESS_TOKEN_KEY)}`;
  // } // users/login uses Authorization header with the email & password encoded for the request

  if (isServer) {
    const { cookies } = await import('next/headers');
    const cookiesString = (await cookies())
      .getAll()
      .map((item) => `${item.name}=${item.value}`)
      .join('; ');
    req.headers.cookie = cookiesString;
    // language = await getLocale();
  } else {
    language =
      Object.fromEntries(
        new URLSearchParams(document.cookie.replace(/; /g, '&'))
      ).NEXT_LOCALE || language;
  }
  req.headers['Accept-Language'] = language;
  return req;
};

const httpInstance = axios.create({
  baseURL: BASE_URL,
  responseType: 'json',
  withCredentials: true,
  timeout: 60000,
  paramsSerializer: { indexes: null }
});

httpInstance.interceptors.request.use(cookiesInterceptor);

httpInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // console.log(error, 'error interceptor');
    // console.log(error.response, 'error response');
    const originalRequest = error.config as CustomAxiosRequestConfig;
    const isRefreshRoute = originalRequest.url?.includes(refreshRoute);
    const isUnauthorizedError = error.response?.status === 401;
    const isServerError =
      error.response?.status === 500 || error.code === 'ERR_NETWORK';
    if (isServerError && document) {
      try {
        const language =
          Object.fromEntries(
            new URLSearchParams(document.cookie.replace(/; /g, '&'))
          ).NEXT_LOCALE || 'en';
        const messages = dictionaries[language as 'en'];
        const t = createTranslator({
          locale: language,
          messages: messages as IntlMessages
        });
        toast({
          title: t('common.error'),
          variant: 'destructive'
        });
      } catch (err) {
        console.log(err, 'err');
      }
    }
    const isAuthRoute = AUTH_ROUTES.some(
      (route) => !isServer && window.location.pathname.includes(route)
    );
    const cleanup = async () => {
      try {
        await setToken('', ACCESS_TOKEN_KEY);
        await setToken('', REFRESH_TOKEN_KEY);
        queryClient.removeQueries();
      } catch (err) {
        console.log(err, 'error cleaning up');
      } finally {
        window.location.href = '/login';
      }
    };
    if (isRefreshRoute && isUnauthorizedError) {
      console.log('isRefreshRoute && isUnauthorizedError');
      await cleanup();
      return Promise.reject(error);
    }
    if (
      isUnauthorizedError &&
      !isRefreshRoute &&
      !isAuthRoute &&
      !originalRequest._retry &&
      !isServer
    ) {
      originalRequest._retry = true;
      try {
        const res = await getRefreshToken();
        if ('response' in res) {
          const axiosRes = res.response as AxiosResponse;
          if (axiosRes.status === 401) {
            throw new Error('Unauthorized');
          }
        }
        // Adaptar la respuesta del backend que tiene estructura: { status_code, content: { data } }
        if (res.data && typeof res.data === 'object' && 'content' in res.data) {
          const backendResponse = res.data as any;
          if (
            backendResponse.content &&
            backendResponse.content.data &&
            backendResponse.content.data.accessToken
          ) {
            await setToken(
              backendResponse.content.data.accessToken,
              ACCESS_TOKEN_KEY
            );
            return httpInstance.request(originalRequest);
          }
        }
      } catch (err) {
        console.log(err, 'err interceptor');
        try {
          await setToken('', ACCESS_TOKEN_KEY);
          await setToken('', REFRESH_TOKEN_KEY);
          queryClient.removeQueries();
        } catch (err) {
          console.log(err);
        } finally {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
    // Sesión caducada
    // debugger;
    if (error.response?.status === 401) {
      const apiError = error.response.data as APIError;
      if (apiError?.error?.errors?.[0]?.reason === 'WrongCredentials') {
        await setToken('', ACCESS_TOKEN_KEY);
        await setToken('', REFRESH_TOKEN_KEY);
        await queryClient.resetQueries();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { httpInstance };
